import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import type { DiffOnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/skeleton';
import { useTheme } from '@/shadcn/theme/theme-provider';

import { DiffStatsDisplay } from '@/components/monaco/diffViewer/toolbar/diffStats.diffViewer';
import { DiffViewToggle } from '@/components/monaco/diffViewer/toolbar/diffViewToggle.diffViewer';
import {
  computeDiffStats,
  type DiffStats
} from '@/components/monaco/diffViewer/utils/diffStats.diffViewer';
import { formatJson } from '@/components/monaco/diffViewer/utils/sort.diffViewer';

const MonacoDiffEditor = lazy(async () => {
  const module = await import('@monaco-editor/react');
  return { default: module.DiffEditor };
});

type Props = {
  before: unknown;
  after: unknown;
  height?: React.CSSProperties['height'];
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

export const DiffViewer = ({ before, after, className, height }: Props) => {
  const { theme } = useTheme();
  const original = useMemo(() => formatJson(before, before), [before]);
  const modified = useMemo(() => formatJson(after, before), [after, before]);
  const editorRef = useRef<editor.IStandaloneDiffEditor | null>(null);
  const [diffStats, setDiffStats] = useState<DiffStats>({
    additions: 0,
    deletions: 0,
    modifications: 0
  });
  const [options, setOptions] = useState({
    renderSideBySide: true
  });

  const handleEditorDidMount: DiffOnMount = diffEditor => {
    editorRef.current = diffEditor;

    const updateDiffStats = () => {
      const lineChanges = diffEditor.getLineChanges();
      setDiffStats(computeDiffStats(lineChanges));
    };

    // Monaco diff editor fires onDidUpdateDiff when diff computation completes
    diffEditor.onDidUpdateDiff(updateDiffStats);

    // Also try to get initial diff (might be ready already)
    updateDiffStats();
  };

  // Re-compute when content changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const updateDiffStats = () => {
      const lineChanges = editor.getLineChanges();
      setDiffStats(computeDiffStats(lineChanges));
    };

    // Small delay to allow diff to recompute after content change
    const timeoutId = setTimeout(updateDiffStats, 100);
    return () => clearTimeout(timeoutId);
  }, [original, modified]);

  return (
    <div
      className={cn('flex h-full w-full grow flex-col', className)}
      style={{ height: '100%', minHeight: height ? undefined : '400px' }}
    >
      <div className="flex items-center justify-end gap-4 border-b bg-muted/50 px-3 py-1.5 text-xs">
        <DiffStatsDisplay diffStats={diffStats} />
        <DiffViewToggle
          onToggle={() =>
            setOptions(prev => ({
              ...prev,
              renderSideBySide: !prev.renderSideBySide
            }))
          }
          renderSideBySide={options.renderSideBySide}
        />
      </div>
      <div className="min-h-0 flex-1">
        <Suspense fallback={<Skeleton className="h-full w-full" />}>
          <MonacoDiffEditor
            className=""
            height="100%"
            keepCurrentModifiedModel
            keepCurrentOriginalModel
            language="json"
            loading={<Skeleton className="h-full w-full" />}
            modified={modified}
            onMount={handleEditorDidMount}
            options={{
              readOnly: true,
              copyWithSyntaxHighlighting: false,
              cursorBlinking: 'smooth',
              cursorSmoothCaretAnimation: 'on',
              renderSideBySide: options.renderSideBySide,
              minimap: { enabled: true },
              codeLens: false,
              folding: true,
              foldingStrategy: 'auto',
              showFoldingControls: 'always',
              glyphMargin: false,
              renderWhitespace: 'selection',
              scrollBeyondLastLine: false,
              overviewRulerBorder: false,
              renderIndicators: false,
              wordWrap: 'on',
              hideUnchangedRegions: {
                enabled: true,
                minimumLineCount: 3,
                contextLineCount: 3
              }
            }}
            original={original}
            theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          />
        </Suspense>
      </div>
    </div>
  );
};
